import type {
  AiDraft,
  AiMode,
  Appointment,
  AppointmentSlot,
  AvailabilityRule,
  AvailabilityRuleCreatePayload,
  AvailabilityRuleUpdatePayload,
  AvailabilityRulesResponse,
  BookAppointmentPayload,
  EffectiveAvailabilityResponse,
  AgendaCalendarResponse,
  AttributionModel,
  AuthUser,
  AuthLoginResponse,
  AuthMeResponse,
  AuthOrganizationsResponse,
  OperationalAuthSession,
  BoardResponse,
  CohortMetric,
  CommercialProduct,
  Contact,
  ContactDetail,
  ConversationDetail,
  ConversationListItem,
  DashboardE,
  DashboardMvpB,
  EconomicCalculation,
  EconomicDashboard,
  ExecutiveAlert,
  ForecastResponse,
  Handoff,
  Id,
  InternalReferral,
  Message,
  OriginType,
  Opportunity,
  PatientIssue,
  PatientLtv,
  PaymentMethod,
  PaymentRecord,
  Pipeline,
  PostcareEventStatus,
  Professional,
  ProfessionalMaster,
  ProfessionalMasterPayload,
  Specialty,
  ProfessionalSensitiveProfile,
  ProfessionalFiscalProfile,
  ProfessionalCredential,
  ProfessionalInsurance,
  ProfessionalPaymentAccount,
  ProfessionalDocument,
  ProfessionalDocumentInitialization,
  ProfessionalDocumentSection,
  ProfessionalDocumentType,
  ProfessionalVerificationStatus,
  ProfessionalServiceSetting,
  ProfessionalServiceSettingsResponse,
  ProfessionalSettlement,
  Quote,
  RetentionDashboard,
  RetentionOpportunity,
  RetentionOpportunityStatus,
  RetentionOpportunityType,
  ReviewRequest,
  ReviewRequestStatus,
  Sale,
  SatisfactionSurvey,
  SavedView,
  SellerSettlement,
  SellerSettlementDetail,
  ProfessionalSettlementDetail,
  RoleCode,
  SettlementPaymentRecord,
  SettlementPaymentPayload,
  Stage,
  StaffUser,
  StaffRole,
  Task,
  TimelineEvent,
  TreatmentEvent,
  TreatmentPostcareResponse,
  ClinicalEncounter,
  ClinicalNote,
  ClinicalRecord,
  ClinicalTimelineResponse,
  Prescription,
  MedicalOrder,
  PatientConsent,
  PatientFile,
  ClinicalAlert,
  ClinicalAlertStatus,
  ClinicalProblem,
  ClinicalProblemStatus,
  Allergy,
  ActiveMedication,
  ActiveMedicationStatus,
  DiagnosticStudy,
  StudyResultFlag,
  AnthropometricMeasurement,
  TreatmentProtocolTemplate,
  TreatmentProtocolRecord,
  ImplantDeviceRecord,
  PatientMedicalProfile,
  TreatmentRecordView,
  TreatmentRecordInsumoInput,
  TreatmentRecordEquipmentInput,
  ServiceTemplatesResponse,
  ConsumptionTemplate,
  EquipmentTemplate,
  EffectivePermissionsResponse,
  EffectivePermission,
  UserPermissionsResponse,
  PermissionOverrideResponse,
  PermissionScope,
  AnthropometricMeasurementContext,
  AnthropometricSourceType,
  ClinicalCareProgramListResponse,
  ClinicalCareProgramDetailResponse,
  Clinical360Report,
  Clinical360ReportSection,
  Clinical360ReportType,
  ClinicalPrivateFileInitialization,
  ClinicalComparisonSet,
  ClinicalComparisonType,
  ClinicalComparisonItemRole,
  ClinicalPatientVisibility,
  ClinicalAnatomicalMap,
  ClinicalAnatomicalMapType,
  ClinicalAnatomicalMapPurpose,
  ClinicalAnatomicalMarkerType,
  ClinicalGeometryType,
  ClinicalLaterality,
} from "./types";

const BASE_URL: string = (import.meta as any).env?.VITE_API_URL || "/api";

export class ApiClientError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

function getToken(): string | null {
  return localStorage.getItem("mauvelore_token");
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem("mauvelore_token", token);
  else localStorage.removeItem("mauvelore_token");
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem("mauvelore_user");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function setStoredUser(user: AuthUser | null) {
  if (user) localStorage.setItem("mauvelore_user", JSON.stringify(user));
  else localStorage.removeItem("mauvelore_user");
}

async function request<T>(
  path: string,
  options: {
    method?: string;
    body?: any;
    query?: Record<string, string | undefined>;
    authToken?: string | null;
  } = {}
): Promise<T> {
  const {
    method = "GET",
    body,
    query,
    authToken,
  } = options;
  let url = `${BASE_URL}${path}`;
  if (query) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.set(k, v);
    });
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token =
    authToken === undefined
      ? getToken()
      : authToken;
  if (token) {
    headers["Authorization"] =
      `Bearer ${token}`;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let data: any = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    const errCode = data?.error || "unknown_error";
    const errMsg = data?.message || `Error ${res.status}`;
    if (res.status === 401) {
      setToken(null);
      setStoredUser(null);
    }
    throw new ApiClientError(res.status, errCode, errMsg);
  }

  return data as T;
}

async function binaryRequest(
  path: string,
  options: {
    method?: string;
    body?: Blob;
    mimeType?: string;
  } = {}
): Promise<Response> {
  const {
    method = "GET",
    body,
    mimeType,
  } = options;

  const headers: Record<string, string> = {};

  const token = getToken();

  if (token) {
    headers.Authorization =
      `Bearer ${token}`;
  }

  if (mimeType) {
    headers["Content-Type"] =
      mimeType;
  }

  const response = await fetch(
    `${BASE_URL}${path}`,
    {
      method,
      headers,
      body,
      cache: "no-store",
    }
  );

  if (!response.ok) {
    let data: any = null;

    try {
      data = await response
        .clone()
        .json();
    } catch {
      // Respuesta sin JSON.
    }

    if (response.status === 401) {
      setToken(null);
      setStoredUser(null);
    }

    throw new ApiClientError(
      response.status,
      data?.error ||
        "unknown_error",
      data?.message ||
        `Error ${response.status}`
    );
  }

  return response;
}

export const api = {
  login: (
    email: string,
    password: string
  ) =>
    request<AuthLoginResponse>(
      "/auth/login",
      {
        method: "POST",
        body: {
          email,
          password,
        },
        authToken: null,
      }
    ),

  selectOrganization: (
    selectionToken: string,
    membershipId: Id
  ) =>
    request<OperationalAuthSession>(
      "/auth/select-organization",
      {
        method: "POST",
        body: {
          membership_id:
            membershipId,
        },
        authToken:
          selectionToken,
      }
    ),

  switchOrganization: (
    membershipId: Id
  ) =>
    request<OperationalAuthSession>(
      "/auth/switch-organization",
      {
        method: "POST",
        body: {
          membership_id:
            membershipId,
        },
      }
    ),

  switchLocation: (
    locationId: Id | null
  ) =>
    request<OperationalAuthSession>(
      "/auth/switch-location",
      {
        method: "POST",
        body: {
          location_id:
            locationId,
        },
      }
    ),

  authOrganizations: () =>
    request<AuthOrganizationsResponse>(
      "/auth/organizations"
    ),

  me: () =>
    request<AuthMeResponse>(
      "/auth/me"
    ),

  authPermissions: () =>
    request<EffectivePermissionsResponse>(
      "/auth/permissions"
    ),

  pipelines: () => request<Pipeline[]>("/pipelines"),
  stages: () => request<Stage[]>("/stages"),
  users: () => request<StaffUser[]>("/users"),
  userRoles: () => request<StaffRole[]>("/users/roles"),

  createUser: (payload: {
    full_name: string;
    email: string;
    phone?: string | null;
    role_code: RoleCode;
    password: string;
  }) =>
    request<StaffUser>("/users", {
      method: "POST",
      body: payload,
    }),

  updateUser: (
    id: Id,
    payload: {
      full_name?: string;
      email?: string;
      phone?: string | null;
      role_code?: RoleCode;
      active?: boolean;
      all_locations?: boolean;
      location_ids?: Id[];
      valid_from?: string;
      valid_until?: string | null;
    }
  ) =>
    request<StaffUser>(`/users/${id}`, {
      method: "PATCH",
      body: payload,
    }),

  resetUserPassword: (id: Id, password: string) =>
    request<{ status: string }>(`/users/${id}/reset-password`, {
      method: "POST",
      body: { password },
    }),

  getUserPermissions: (id: Id) =>
    request<UserPermissionsResponse>(`/users/${id}/permissions`),

  setUserPermission: (
    id: Id,
    permissionCode: string,
    payload: {
      allowed: boolean;
      scope?: PermissionScope;
    }
  ) =>
    request<PermissionOverrideResponse>(
      `/users/${id}/permissions/${permissionCode}`,
      {
        method: "PUT",
        body: payload,
      }
    ),

  removeUserPermissionOverride: (
    id: Id,
    permissionCode: string
  ) =>
    request<{
      status: string;
      effective: EffectivePermission;
    }>(
      `/users/${id}/permissions/${permissionCode}`,
      {
        method: "DELETE",
      }
    ),

  // Maestro Integral de Profesionales — Fase 2D.
  professionalMasterList: () =>
    request<{
      scope: string;
      filters: {
        active: boolean | null;
        specialty_id: Id | null;
        user_id: Id | null;
        search: string | null;
      };
      professionals: ProfessionalMaster[];
    }>(
      "/professional-master/professionals"
    ).then(
      (response) =>
        response.professionals
    ),

  professionalMasterDetail: (id: Id) =>
    request<ProfessionalMaster>(
      `/professional-master/professionals/${id}`
    ),

  professionalSpecialties: () =>
    request<Specialty[]>(
      "/professional-master/specialties"
    ),

  createProfessionalSpecialty: (
    payload: {
      code?: string;
      name: string;
      category?: string | null;
      description?: string | null;
      active?: boolean;
    }
  ) =>
    request<Specialty>(
      "/professional-master/specialties",
      {
        method: "POST",
        body: payload,
      }
    ),

  updateProfessionalSpecialty: (
    id: Id,
    payload: {
      code?: string;
      name?: string;
      category?: string | null;
      description?: string | null;
      active?: boolean;
    }
  ) =>
    request<Specialty>(
      `/professional-master/specialties/${id}`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  createProfessional: (
    payload: ProfessionalMasterPayload & {
      full_name: string;
    }
  ) =>
    request<ProfessionalMaster>(
      "/professional-master/professionals",
      {
        method: "POST",
        body: payload,
      }
    ),

  updateProfessional: (
    id: Id,
    payload: ProfessionalMasterPayload
  ) =>
    request<ProfessionalMaster>(
      `/professional-master/professionals/${id}`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  getProfessionalSensitiveProfile: (
    id: Id
  ) =>
    request<{
      professional: {
        id: Id;
        full_name: string;
        active: boolean;
      };
      profile:
        ProfessionalSensitiveProfile | null;
    }>(
      `/professional-master/professionals/${id}/sensitive-profile`
    ).then(
      (response) => response.profile
    ),

  updateProfessionalSensitiveProfile: (
    id: Id,
    payload: Partial<
      Omit<
        ProfessionalSensitiveProfile,
        | "professional_id"
        | "created_at"
        | "updated_at"
      >
    >
  ) =>
    request<ProfessionalSensitiveProfile>(
      `/professional-master/professionals/${id}/sensitive-profile`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  getProfessionalFiscalProfile: (
    id: Id
  ) =>
    request<{
      professional_id: Id;
      fiscal_profile:
        ProfessionalFiscalProfile | null;
    }>(
      `/professional-master/professionals/${id}/fiscal-profile`
    ).then(
      (response) =>
        response.fiscal_profile
    ),

  updateProfessionalFiscalProfile: (
    id: Id,
    payload: Partial<
      Omit<
        ProfessionalFiscalProfile,
        | "professional_id"
        | "created_at"
        | "updated_at"
      >
    >
  ) =>
    request<ProfessionalFiscalProfile>(
      `/professional-master/professionals/${id}/fiscal-profile`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  getProfessionalCredentials: (
    id: Id
  ) =>
    request<ProfessionalCredential[]>(
      `/professional-master/professionals/${id}/credentials`
    ),

  createProfessionalCredential: (
    id: Id,
    payload: {
      credential_type: string;
      title: string;
      institution?: string | null;
      registration_number?: string | null;
      jurisdiction?: string | null;
      issued_on?: string | null;
      expires_on?: string | null;
      status?: ProfessionalVerificationStatus;
      storage_key?: string | null;
      notes?: string | null;
      active?: boolean;
    }
  ) =>
    request<ProfessionalCredential>(
      `/professional-master/professionals/${id}/credentials`,
      {
        method: "POST",
        body: payload,
      }
    ),

  updateProfessionalCredential: (
    professionalId: Id,
    credentialId: Id,
    payload: Partial<
      Omit<
        ProfessionalCredential,
        | "id"
        | "professional_id"
        | "verified_at"
        | "verified_by_user_id"
        | "created_at"
        | "updated_at"
      >
    >
  ) =>
    request<ProfessionalCredential>(
      `/professional-master/professionals/${professionalId}/credentials/${credentialId}`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  getProfessionalInsurances: (
    id: Id
  ) =>
    request<ProfessionalInsurance[]>(
      `/professional-master/professionals/${id}/insurances`
    ),

  createProfessionalInsurance: (
    id: Id,
    payload: {
      insurer_name: string;
      policy_number: string;
      coverage_description?: string | null;
      starts_on?: string | null;
      expires_on?: string | null;
      status?: ProfessionalVerificationStatus;
      storage_key?: string | null;
      notes?: string | null;
      active?: boolean;
    }
  ) =>
    request<ProfessionalInsurance>(
      `/professional-master/professionals/${id}/insurances`,
      {
        method: "POST",
        body: payload,
      }
    ),

  updateProfessionalInsurance: (
    professionalId: Id,
    insuranceId: Id,
    payload: Partial<
      Omit<
        ProfessionalInsurance,
        | "id"
        | "professional_id"
        | "verified_at"
        | "verified_by_user_id"
        | "created_at"
        | "updated_at"
      >
    >
  ) =>
    request<ProfessionalInsurance>(
      `/professional-master/professionals/${professionalId}/insurances/${insuranceId}`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  getProfessionalPaymentAccounts: (
    id: Id
  ) =>
    request<ProfessionalPaymentAccount[]>(
      `/professional-master/professionals/${id}/payment-accounts`
    ),

  createProfessionalPaymentAccount: (
    id: Id,
    payload: {
      account_type?: string;
      bank_name?: string | null;
      holder_name: string;
      holder_tax_id?: string | null;
      cbu_cvu?: string | null;
      account_alias?: string | null;
      currency?: string;
      is_primary?: boolean;
      active?: boolean;
    }
  ) =>
    request<ProfessionalPaymentAccount>(
      `/professional-master/professionals/${id}/payment-accounts`,
      {
        method: "POST",
        body: payload,
      }
    ),

  updateProfessionalPaymentAccount: (
    professionalId: Id,
    accountId: Id,
    payload: Partial<
      Omit<
        ProfessionalPaymentAccount,
        | "id"
        | "professional_id"
        | "created_at"
        | "updated_at"
      >
    >
  ) =>
    request<ProfessionalPaymentAccount>(
      `/professional-master/professionals/${professionalId}/payment-accounts/${accountId}`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  professionalDocuments: (
    id: Id
  ) =>
    request<{
      scope: string;
      professional: {
        id: Id;
        full_name: string;
        active: boolean;
      };
      documents:
        ProfessionalDocument[];
    }>(
      `/professional-master/professionals/${id}/documents`
    ).then(
      (response) =>
        response.documents
    ),

  initializeProfessionalDocument: (
    id: Id,
    payload: {
      section:
        ProfessionalDocumentSection;
      document_type:
        ProfessionalDocumentType;
      original_filename: string;
      mime_type: string;
      size_bytes: number;
      credential_id?: Id | null;
      insurance_id?: Id | null;
      description?: string | null;
      issued_on?: string | null;
      expires_on?: string | null;
    }
  ) =>
    request<
      ProfessionalDocumentInitialization
    >(
      `/professional-master/professionals/${id}/documents`,
      {
        method: "POST",
        body: payload,
      }
    ),

  uploadProfessionalDocumentContent: async (
    professionalId: Id,
    documentId: Id,
    file: File,
    mimeType: string = file.type
  ) => {
    const response =
      await binaryRequest(
        `/professional-master/professionals/${professionalId}/documents/${documentId}/content`,
        {
          method: "PUT",
          body: file,
          mimeType,
        }
      );

    return (
      await response.json()
    ) as ProfessionalDocument;
  },

  downloadProfessionalDocument: async (
    professionalId: Id,
    documentId: Id
  ) => {
    const response =
      await binaryRequest(
        `/professional-master/professionals/${professionalId}/documents/${documentId}/content`
      );

    return response.blob();
  },

  updateProfessionalDocumentMetadata: (
    professionalId: Id,
    documentId: Id,
    payload: {
      section?:
        ProfessionalDocumentSection;
      document_type?:
        ProfessionalDocumentType;
      credential_id?: Id | null;
      insurance_id?: Id | null;
      description?: string | null;
      issued_on?: string | null;
      expires_on?: string | null;
    }
  ) =>
    request<ProfessionalDocument>(
      `/professional-master/professionals/${professionalId}/documents/${documentId}`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  deleteProfessionalDocument: (
    professionalId: Id,
    documentId: Id
  ) =>
    request<{
      status: "deleted";
      document: {
        id: Id;
        professional_id: Id;
        status: "DELETED";
        deleted_at: string;
      };
    }>(
      `/professional-master/professionals/${professionalId}/documents/${documentId}`,
      {
        method: "DELETE",
      }
    ),

  createLead: (payload: {
    full_name: string;
    phone: string;
    email?: string;
    source_channel: string;
    source_campaign?: string;
    service_code: string;
    owner_user_id?: Id;
    force?: boolean;
  }) => request<{ duplicate?: boolean; contact: Contact; lead?: any; opportunity?: Opportunity }>("/leads", { method: "POST", body: payload }),

  searchContacts: (search: string) => request<Contact[]>("/contacts", { query: { search } }),
  // Real shape: { contact, opportunities, tags } — see ContactDetail.
  getContact: (id: Id) => request<ContactDetail>(`/contacts/${id}`),
  getTimeline: (id: Id) => request<TimelineEvent[]>(`/contacts/${id}/timeline`),

  getBoard: (params: {
    pipeline?: string;
    owner?: string;
    source?: string;
    service?: string;
    temperature?: string;
    search?: string;
  }) => request<BoardResponse>("/board", { query: params as Record<string, string | undefined> }),

  getOpportunity: (id: Id) => request<Opportunity>(`/opportunities/${id}`),
  createOpportunity: (payload: Partial<Opportunity> & { contact_id: Id }) =>
    request<Opportunity>("/opportunities", { method: "POST", body: payload }),
  patchOpportunity: (id: Id, payload: Partial<Opportunity>) =>
    request<Opportunity>(`/opportunities/${id}`, { method: "PATCH", body: payload }),
  moveStage: (id: Id, payload: { to_stage_code: string; reason?: string; owner_user_id?: Id }) =>
    request<Opportunity>(`/opportunities/${id}/stage`, { method: "PATCH", body: payload }),
  assign: (id: Id, payload: { owner_user_id?: Id; collaborator_user_ids?: Id[] }) =>
    request<Opportunity>(`/opportunities/${id}/assign`, { method: "POST", body: payload }),

  addNote: (payload: { opportunity_id: Id; body: string }) => request<any>("/notes", { method: "POST", body: payload }),
  addTask: (payload: { opportunity_id: Id; title: string; due_at: string; assigned_user_id: Id; priority?: string }) =>
    request<Task>("/tasks", { method: "POST", body: payload }),
  getTasks: (scope: "mine" | "today" | "overdue" | "team") => request<Task[]>("/tasks", { query: { scope } }),
  completeTask: (id: Id) => request<Task>(`/tasks/${id}/complete`, { method: "PATCH" }),

  getViews: () => request<SavedView[]>("/views"),
  createView: (payload: { name: string; filters: Record<string, string> }) =>
    request<SavedView>("/views", { method: "POST", body: payload }),

  slaBreaches: () => request<Opportunity[]>("/sla/breaches"),

  // ---- MVP-B: Inbox / conversations ----
  getConversations: (scope: "mine" | "unassigned" | "all") =>
    request<ConversationListItem[]>("/conversations", { query: { scope } }),
  getConversation: (id: Id) => request<ConversationDetail>(`/conversations/${id}`),
  getConversationMessages: (id: Id, before?: string) =>
    request<Message[]>(`/conversations/${id}/messages`, { query: { before } }),
  sendConversationMessage: (id: Id, body: string) =>
    request<Message>(`/conversations/${id}/messages`, { method: "POST", body: { body } }),
  setConversationMode: (id: Id, ai_mode: AiMode) =>
    request<ConversationDetail>(`/conversations/${id}/mode`, { method: "PATCH", body: { ai_mode } }),
  acceptHandoff: (id: Id) =>
    request<{ handoff: Handoff; conversation: ConversationDetail }>(`/conversations/${id}/handoff/accept`, {
      method: "POST",
    }),
  getAiDraft: (id: Id) => request<AiDraft | null>(`/conversations/${id}/ai-draft`),

  // ---- MVP-B: dev/demo tools ----
  simulateInbound: (payload: { phone: string; text: string }) =>
    request<any>("/dev/simulate-inbound", { method: "POST", body: payload }),

  // ---- MVP-B: agenda ----
  getAgendaCalendar: (params: {
    from: string;
    to: string;
    professional_id?: Id;
    location_id?: Id;
  }) =>
    request<AgendaCalendarResponse>("/agenda/calendar", {
      query: {
        from: params.from,
        to: params.to,
        professional_id:
          params.professional_id !== undefined
            ? String(params.professional_id)
            : undefined,
        location_id:
          params.location_id !== undefined
            ? String(params.location_id)
            : undefined,
      },
    }),

  getProfessionals: () => request<Professional[]>("/professionals"),
  getSlots: (professionalId: Id, params?: { from?: string; to?: string; type?: string }) =>
    request<AppointmentSlot[]>(`/professionals/${professionalId}/slots`, { query: params }),
  getProfessionalServiceSettings: (params?: {
    professional_id?: Id;
    service_id?: Id;
    active?: boolean;
  }) =>
    request<ProfessionalServiceSettingsResponse>(
      "/agenda/professional-service-settings",
      {
        query: params
          ? {
              professional_id:
                params.professional_id !== undefined
                  ? String(params.professional_id)
                  : undefined,
              service_id:
                params.service_id !== undefined
                  ? String(params.service_id)
                  : undefined,
              active:
                params.active !== undefined
                  ? String(params.active)
                  : undefined,
            }
          : undefined,
      }
    ),

  saveProfessionalServiceSetting: (
    professionalId: Id,
    serviceId: Id,
    payload: {
      duration_minutes: number;
      buffer_before_minutes?: number;
      buffer_after_minutes?: number;
      active?: boolean;
    }
  ) =>
    request<ProfessionalServiceSetting>(
      `/agenda/professionals/${professionalId}/services/${serviceId}`,
      {
        method: "PUT",
        body: payload,
      }
    ),

  getAvailabilityRules: (params?: {
    entity_type?: "PROFESSIONAL" | "RESOURCE";
    entity_id?: Id;
    location_id?: Id;
    service_id?: Id;
    active?: boolean;
  }) =>
    request<AvailabilityRulesResponse>(
      "/agenda/availability-rules",
      {
        query: params
          ? {
              entity_type: params.entity_type,
              entity_id:
                params.entity_id !== undefined
                  ? String(params.entity_id)
                  : undefined,
              location_id:
                params.location_id !== undefined
                  ? String(params.location_id)
                  : undefined,
              service_id:
                params.service_id !== undefined
                  ? String(params.service_id)
                  : undefined,
              active:
                params.active !== undefined
                  ? String(params.active)
                  : undefined,
            }
          : undefined,
      }
    ),

  createAvailabilityRule: (
    payload: AvailabilityRuleCreatePayload
  ) =>
    request<AvailabilityRule>(
      "/agenda/availability-rules",
      {
        method: "POST",
        body: payload,
      }
    ),

  updateAvailabilityRule: (
    ruleId: Id,
    payload: AvailabilityRuleUpdatePayload
  ) =>
    request<AvailabilityRule>(
      `/agenda/availability-rules/${ruleId}`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  deactivateAvailabilityRule: (ruleId: Id) =>
    request<AvailabilityRule>(
      `/agenda/availability-rules/${ruleId}`,
      {
        method: "DELETE",
      }
    ),

  getEffectiveAvailability: (params: {
    from: string;
    to: string;
    service_id: Id;
    professional_id?: Id;
    location_id?: Id;
  }) =>
    request<EffectiveAvailabilityResponse>(
      "/agenda/effective-availability",
      {
        query: {
          from: params.from,
          to: params.to,
          service_id: String(params.service_id),
          professional_id:
            params.professional_id !== undefined
              ? String(params.professional_id)
              : undefined,
          location_id:
            params.location_id !== undefined
              ? String(params.location_id)
              : undefined,
        },
      }
    ),

  getAppointments: (params?: {
    professional_id?: Id;
    status?: string;
    opportunity_id?: Id;
    from?: string;
    to?: string;
  }) =>
    request<Appointment[]>("/appointments", {
      query: params
        ? {
            professional_id:
              params.professional_id !== undefined
                ? String(params.professional_id)
                : undefined,
            status: params.status,
            opportunity_id:
              params.opportunity_id !== undefined
                ? String(params.opportunity_id)
                : undefined,
            from: params.from,
            to: params.to,
          }
        : undefined,
    }),
  bookAppointment: (payload: BookAppointmentPayload) =>
    request<Appointment>("/appointments", {
      method: "POST",
      body: payload,
    }),

  cancelAppointment: (appointmentId: Id, payload?: { reason?: string }) =>
    request<Appointment>(`/appointments/${appointmentId}/cancel`, {
      method: "PATCH",
      body: payload || {},
    }),

  rescheduleAppointment: (
    appointmentId: Id,
    payload: { slot_id: Id; resource_slot_ids?: Id[] }
  ) =>
    request<Appointment>(`/appointments/${appointmentId}/reschedule`, {
      method: "PATCH",
      body: payload,
    }),

  // ---- MVP-B: dashboard ----
  getDashboardMvpB: (params?: { seller?: string; service?: string; campaign?: string; from?: string; to?: string }) =>
    request<DashboardMvpB>("/dashboard/mvp-b", { query: params }),

  // ---- MVP-C: commercial products ----
  getCommercialProducts: () => request<CommercialProduct[]>("/commercial-products"),

  createCommercialProduct: (payload: {
    service_id?: Id | null;
    code: string;
    name: string;
    category?: string | null;
    family?: string | null;
    active?: boolean;
    direct_cost?: number;
    professional_fee?: number;
    other_variable_cost?: number;
    list_price: number;
    cash_or_transfer_price?: number | null;
    card_1_price?: number | null;
    card_6_price?: number | null;
    protected_floor_cash_or_transfer?: number | null;
    protected_floor_card_1?: number | null;
    protected_floor_card_6?: number | null;
    recommended_price_cash_or_transfer?: number | null;
    recommended_price_card_1?: number | null;
    recommended_price_card_6?: number | null;
    max_safe_seller_pct?: number | null;
  }) =>
    request<CommercialProduct>("/commercial-products", {
      method: "POST",
      body: payload,
    }),

  updateCommercialProduct: (
    productId: Id,
    payload: Partial<{
      service_id: Id | null;
      code: string;
      name: string;
      category: string | null;
      family: string | null;
      active: boolean;
      direct_cost: number;
      professional_fee: number;
      other_variable_cost: number;
      list_price: number;
      cash_or_transfer_price: number | null;
      card_1_price: number | null;
      card_6_price: number | null;
      protected_floor_cash_or_transfer: number | null;
      protected_floor_card_1: number | null;
      protected_floor_card_6: number | null;
      recommended_price_cash_or_transfer: number | null;
      recommended_price_card_1: number | null;
      recommended_price_card_6: number | null;
      max_safe_seller_pct: number | null;
    }>
  ) =>
    request<CommercialProduct>(`/commercial-products/${productId}`, {
      method: "PATCH",
      body: payload,
    }),

  // ---- Economic parameters / versioned financial rules ----
  getEconomicParameters: () =>
    request<any[]>("/economic-parameters"),

  getCurrentEconomicParameters: () =>
    request<any | null>("/economic-parameters/current"),

  createEconomicParameters: (payload: {
    valid_from: string;
    vat_treatment_pct: number;
    iibb_pct: number;
    card_1_fee_pct: number;
    card_1_fee_vat_pct: number;
    card_6_fee_pct: number;
    card_6_fee_vat_pct: number;
    integration_fund_pct: number;
    max_seller_pct: number;
    max_total_incentives_pct: number;
    min_mauvelore_margin_pct: number;
    target_mauvelore_margin_pct: number;
  }) =>
    request<any>("/economic-parameters", {
      method: "POST",
      body: payload,
    }),

  // ---- Payment conditions / financial configuration ----
  getPaymentConditions: (params?: { active?: boolean }) =>
    request<any[]>("/payment-conditions", {
      query: {
        active: params?.active ? "true" : undefined,
      },
    }),

  createPaymentCondition: (payload: {
    code: string;
    name: string;
    provider?: string | null;
    payment_method: PaymentMethod;
    installments: number;
    card_brand?: string | null;
    bank_name?: string | null;
    promotion_name?: string | null;
    fee_pct: number;
    fee_vat_pct: number;
    additional_pct: number;
    fixed_fee: number;
    valid_from: string;
    valid_to?: string | null;
    active?: boolean;
  }) =>
    request<any>("/payment-conditions", {
      method: "POST",
      body: payload,
    }),

  updatePaymentCondition: (
    id: Id,
    payload: {
      name?: string;
      valid_to?: string | null;
      active?: boolean;
    }
  ) =>
    request<any>(`/payment-conditions/${id}`, {
      method: "PATCH",
      body: payload,
    }),

  // ---- MVP-C: live guardrail/economics preview (no persistence) ----
  calculateEconomics: (payload: {
    product_id: Id;
    payment_method: PaymentMethod;
    payment_condition_id?: Id;
    origin_type: OriginType;
    discount_pct?: number;
    seller_id?: Id;
  }) => request<EconomicCalculation>("/economic/calculate", { method: "POST", body: payload }),

  // ---- MVP-C: quotes ----
  getQuotes: (params?: { opportunity_id?: Id; status?: string }) =>
    request<Quote[]>("/quotes", { query: params as Record<string, string | undefined> }),
  getQuote: (id: Id) => request<Quote>(`/quotes/${id}`),
  createQuote: (payload: {
    opportunity_id: Id;
    product_id: Id;
    payment_method: PaymentMethod;
    payment_condition_id?: Id;
    origin_type: OriginType;
    discount_pct?: number;
    seller_id?: Id;
    professional_id?: Id;
    valid_until?: string;
  }) => request<Quote>("/quotes", { method: "POST", body: payload }),
  sendQuote: (id: Id) => request<Quote>(`/quotes/${id}/send`, { method: "POST" }),
  overrideQuote: (id: Id, payload: { reason: string; new_final_price?: number; approved_by_user_id?: Id }) =>
    request<Quote>(`/quotes/${id}/override`, { method: "POST", body: payload }),
  acceptQuote: (id: Id) => request<Sale>(`/quotes/${id}/accept`, { method: "POST" }),

  // ---- MVP-C: sales & payments ----
  getSales: (params?: { status?: string; seller_id?: Id }) =>
    request<Sale[]>("/sales", { query: params as Record<string, string | undefined> }),
  getSale: (id: Id) => request<Sale>(`/sales/${id}`),
  registerPayment: (
    saleId: Id,
    payload: { amount: number; payment_method: PaymentMethod; payment_condition_id?: Id; installments?: number; external_reference?: string; paid_at?: string }
  ) => request<{ payment: PaymentRecord; snapshot: any; sale_status: string }>(`/sales/${saleId}/payments`, { method: "POST", body: payload }),
  registerRefund: (saleId: Id, payload: { payment_id: Id; amount: number; reason?: string }) =>
    request<{ refund_payment: PaymentRecord; snapshot: any; sale_status: string }>(`/sales/${saleId}/refunds`, {
      method: "POST",
      body: payload,
    }),
  cancelSale: (id: Id) => request<Sale>(`/sales/${id}`, { method: "PATCH", body: { status: "CANCELLED" } }),

  // ---- MVP-C: settlements ----
  getSellerSettlements: (month?: string) => request<SellerSettlement[]>("/settlements/sellers", { query: { month } }),
  generateSellerSettlements: (month?: string) =>
    request<SellerSettlement[]>("/settlements/sellers/generate", { method: "POST", query: { month } }),
  approveSellerSettlement: (id: Id) => request<SellerSettlement>(`/settlements/sellers/${id}/approve`, { method: "POST" }),
  lockSellerSettlement: (id: Id) => request<SellerSettlement>(`/settlements/sellers/${id}/lock`, { method: "POST" }),

  getProfessionalSettlements: (month?: string) =>
    request<ProfessionalSettlement[]>("/settlements/professionals", { query: { month } }),
  generateProfessionalSettlements: (month?: string) =>
    request<ProfessionalSettlement[]>("/settlements/professionals/generate", { method: "POST", query: { month } }),
  approveProfessionalSettlement: (id: Id) =>
    request<ProfessionalSettlement>(`/settlements/professionals/${id}/approve`, { method: "POST" }),
  lockProfessionalSettlement: (id: Id) =>
    request<ProfessionalSettlement>(`/settlements/professionals/${id}/lock`, { method: "POST" }),

  getSellerSettlement: (id: Id) =>
    request<SellerSettlementDetail>(
      `/settlements/sellers/${id}`
    ),

  registerSellerSettlementPayment: (
    id: Id,
    payload: SettlementPaymentPayload
  ) =>
    request<{
      payment: SettlementPaymentRecord;
      settlement: SellerSettlementDetail;
    }>(
      `/settlements/sellers/${id}/payments`,
      { method: "POST", body: payload }
    ),

  getProfessionalSettlement: (id: Id) =>
    request<ProfessionalSettlementDetail>(
      `/settlements/professionals/${id}`
    ),

  registerProfessionalSettlementPayment: (
    id: Id,
    payload: SettlementPaymentPayload
  ) =>
    request<{
      payment: SettlementPaymentRecord;
      settlement: ProfessionalSettlementDetail;
    }>(
      `/settlements/professionals/${id}/payments`,
      { method: "POST", body: payload }
    ),

  reverseSettlementPayment: (
    paymentId: Id,
    payload?: { notes?: string }
  ) =>
    request<{
      reversal: SettlementPaymentRecord;
      settlement:
        | SellerSettlementDetail
        | ProfessionalSettlementDetail;
    }>(
      `/settlements/payments/${paymentId}/reverse`,
      {
        method: "POST",
        body: payload || {},
      }
    ),

  // ---- MVP-C: economics dashboard ----
  getDashboardEconomic: (params?: { from?: string; to?: string; seller?: string; product?: string; campaign?: string }) =>
    request<EconomicDashboard>("/dashboard/economic", { query: params }),

  // ---- MVP-C: referrals ----
  getReferrals: () => request<InternalReferral[]>("/referrals"),
  createReferral: (payload: {
    patient_id: Id;
    opportunity_id?: Id;
    source_professional_id: Id;
    target_professional_id?: Id;
    target_area: string;
  }) => request<InternalReferral>("/referrals", { method: "POST", body: payload }),
  completeReferralEvaluation: (id: Id) => request<{ referral: InternalReferral; points_awarded: any }>(`/referrals/${id}/complete-evaluation`, { method: "POST" }),

  // ---- MVP-D: treatments / postcare ----
  registerTreatment: (payload: {
    contact_id: Id;
    product_id: Id;
    sale_id?: Id;
    appointment_id?: Id;
    opportunity_id?: Id;
    professional_id?: Id;
    performed_at?: string;
    protocol_version?: string;
    notes?: string;
  }) =>
    request<{ treatment_event: TreatmentEvent; postcare_events: any[]; retention_opportunities: any[] }>("/treatments", {
      method: "POST",
      body: payload,
    }),
  getTreatmentPostcare: (id: Id) => request<TreatmentPostcareResponse>(`/treatments/${id}/postcare`),

  // ---- MVP-D: patient issues ----
  getIssues: (params?: { status?: string; severity?: string; contact_id?: Id }) =>
    request<PatientIssue[]>("/issues", { query: params as Record<string, string | undefined> }),
  createIssue: (payload: { contact_id: Id; issue_type: string; description?: string; severity?: "NORMAL" | "URGENT"; treatment_event_id?: Id }) =>
    request<PatientIssue>("/issues", { method: "POST", body: payload }),
  resolveIssue: (id: Id, payload: { resolution_notes?: string }) =>
    request<PatientIssue>(`/issues/${id}/resolve`, { method: "POST", body: payload }),

  // ---- MVP-D: satisfaction & reviews ----
  submitSatisfaction: (payload: { treatment_event_id: Id; csat?: number; nps?: number; free_text?: string }) =>
    request<SatisfactionSurvey>("/satisfaction", { method: "POST", body: payload }),
  requestReview: (payload: { treatment_event_id: Id; platform?: string }) =>
    request<ReviewRequest>("/reviews/request", { method: "POST", body: payload }),
  getReviewRequests: (params?: { status?: string; contact_id?: Id }) =>
    request<ReviewRequest[]>("/reviews/requests", { query: params as Record<string, string | undefined> }),
  progressReviewRequest: (
    id: Id,
    payload: { status: "CLICKED" | "RECEIVED" | "DECLINED"; rating?: number; review_text?: string; review_url?: string; marketing_consent?: boolean }
  ) => request<ReviewRequest>(`/reviews/${id}`, { method: "PATCH", body: payload }),

  // ---- MVP-D: retention / rebuy opportunities ----
  getRetentionOpportunities: (params?: { status?: string; contact_id?: Id; opportunity_type?: RetentionOpportunityType; upcoming?: boolean }) =>
    request<RetentionOpportunity[]>("/retention/opportunities", {
      query: {
        status: params?.status,
        contact_id: params?.contact_id !== undefined ? String(params.contact_id) : undefined,
        opportunity_type: params?.opportunity_type,
        upcoming: params?.upcoming ? "true" : undefined,
      },
    }),
  getRetentionOpportunity: (id: Id) => request<RetentionOpportunity>(`/retention/opportunities/${id}`),
  assignRetentionOpportunity: (id: Id, payload: { seller_id: Id }) =>
    request<RetentionOpportunity>(`/retention/opportunities/${id}/assign`, { method: "POST", body: payload }),
  closeRetentionOpportunity: (id: Id, payload: { status: "WON" | "LOST"; resulting_opportunity_id?: Id; resulting_sale_id?: Id }) =>
    request<RetentionOpportunity>(`/retention/opportunities/${id}/close`, { method: "POST", body: payload }),

  // ---- MVP-D: LTV / cohorts / retention dashboard ----
  getPatientLtv: (id: Id) => request<PatientLtv>(`/patients/${id}/ltv`),
  getCohorts: (params?: { key?: string; month?: string }) => request<CohortMetric[]>("/cohorts", { query: params }),
  getRetentionDashboard: (params?: { from?: string; to?: string; cohort?: string }) =>
    request<RetentionDashboard>("/dashboard/retention", { query: params }),

  // ---- MVP-E: executive dashboard / marketing intelligence ----
  // Every GET below accepts the same `from`/`to` (+ optional
  // `compareFrom`/`compareTo`, test #29) period-comparison pair per the
  // contract's "thin wrapper" rule — kept as a loose Record so each screen
  // can pass whatever extra filter (seller_id, campaign, dimension, etc.)
  // its own endpoint defines without a bespoke params type per call.
  getExecutiveDashboard: (params?: Record<string, string | undefined>) =>
    request<DashboardE>("/dashboard/executive", { query: params }),
  getFunnelDashboard: (params?: Record<string, string | undefined>) =>
    request<DashboardE>("/dashboard/funnel", { query: params }),
  getMarketingDashboard: (params?: Record<string, string | undefined>) =>
    request<DashboardE>("/dashboard/marketing", { query: params }),
  getSellersDashboard: (params?: Record<string, string | undefined>) =>
    request<DashboardE>("/dashboard/sellers", { query: params }),
  getProfessionalsDashboard: (params?: Record<string, string | undefined>) =>
    request<DashboardE>("/dashboard/professionals", { query: params }),
  getTreatmentsDashboard: (params?: Record<string, string | undefined>) =>
    request<DashboardE>("/dashboard/treatments", { query: params }),
  getFinanceDashboard: (params?: Record<string, string | undefined>) =>
    request<DashboardE>("/dashboard/finance", { query: params }),
  getLtvEDashboard: (params?: Record<string, string | undefined>) =>
    request<DashboardE>("/dashboard/ltv", { query: params }),

  getForecast: (params?: Record<string, string | undefined>) => request<ForecastResponse>("/forecast", { query: params }),
  setForecastProbability: (stageCode: string, probability: number) =>
    request<any>(`/forecast/probabilities/${stageCode}`, { method: "PATCH", body: { probability } }),
  snapshotForecast: () => request<any>("/forecast/snapshot", { method: "POST" }),

  getAlerts: (params?: { status?: string }) => request<ExecutiveAlert[]>("/alerts", { query: params }),
  acknowledgeAlert: (id: Id) => request<ExecutiveAlert>(`/alerts/${id}/acknowledge`, { method: "POST" }),
  evaluateAlerts: () => request<any>("/alerts/evaluate", { method: "POST" }),

  createTouchpoint: (payload: Record<string, any>) => request<any>("/marketing/touchpoints", { method: "POST", body: payload }),
  importMarketingSpend: (rows: Record<string, any>[]) => request<any>("/marketing/spend", { method: "POST", body: rows }),
  recalculateAttribution: (params: { from: string; to: string; model?: AttributionModel }) =>
    request<any>("/attribution/recalculate", { method: "POST", query: params as any }),

  // Generic passthrough used by the drill-down modal to follow a
  // `{endpoint, params}` pointer returned inline on a metric — the backend
  // reuses real MVP-A/B/C/D list endpoints for this (test #30) rather than
  // parallel drilldown-only endpoints, so there's no fixed response type.
  drilldown: (endpoint: string, params?: Record<string, any>) => {
    const path = endpoint.startsWith("/api/") ? endpoint.slice(4) : endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    const query: Record<string, string | undefined> = {};
    if (params) Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null) query[k] = String(v); });
    return request<any>(path, { query });
  },

  // ---- Fase 1 clínica: Historia Clínica 360° (Migración 12) ----
  openEncounter: (payload: {
    contact_id: Id;
    professional_id: Id;
    location_id?: Id;
    encounter_type: string;
    appointment_id?: Id;
    opportunity_id?: Id;
    chief_complaint?: string;
  }) => request<ClinicalEncounter>("/clinical/encounters", { method: "POST", body: payload }),

  createClinicalNote: (
    encounterId: Id,
    payload: {
      professional_id: Id;
      subjective_text?: string;
      objective_text?: string;
      assessment_text?: string;
      plan_text?: string;
      free_text?: string;
      ai_generated?: boolean;
      source_transcript_id?: Id;
    }
  ) => request<ClinicalNote>(`/clinical/encounters/${encounterId}/notes`, { method: "POST", body: payload }),
  signClinicalNote: (noteId: Id) => request<ClinicalNote>(`/clinical/notes/${noteId}/sign`, { method: "POST" }),
  amendClinicalNote: (
    noteId: Id,
    payload: {
      professional_id: Id;
      subjective_text?: string;
      objective_text?: string;
      assessment_text?: string;
      plan_text?: string;
      free_text?: string;
    }
  ) => request<ClinicalNote>(`/clinical/notes/${noteId}/amend`, { method: "POST", body: payload }),

  getClinicalRecord: (contactId: Id) => request<ClinicalRecord>(`/clinical/patients/${contactId}/record`),
  getClinicalTimeline: (contactId: Id) => request<ClinicalTimelineResponse>(`/clinical/patients/${contactId}/timeline`),
  getPatientPrescriptions: (contactId: Id) => request<Prescription[]>(`/clinical/patients/${contactId}/prescriptions`),
  getPatientMedicalOrders: (contactId: Id) => request<MedicalOrder[]>(`/clinical/patients/${contactId}/medical-orders`),
  getPatientConsents: (contactId: Id) => request<PatientConsent[]>(`/clinical/patients/${contactId}/consents`),
  getPatientFiles: (contactId: Id) => request<PatientFile[]>(`/clinical/patients/${contactId}/files`),

  // Fase 1B backend (checkpoint posterior a Migración 14, 2026-08-23):
  // clinical_alerts es la única fuente de verdad de alertas clínicas.
  getClinicalAlerts: (contactId: Id, statuses?: ClinicalAlertStatus[]) =>
    request<ClinicalAlert[]>(
      `/clinical/clinical-alerts?contact_id=${contactId}${statuses ? `&status=${statuses.join(",")}` : ""}`
    ),
  acknowledgeClinicalAlert: (alertId: Id) =>
    request<ClinicalAlert>(`/clinical/clinical-alerts/${alertId}/acknowledge`, { method: "POST" }),
  resolveClinicalAlert: (alertId: Id) =>
    request<ClinicalAlert>(`/clinical/clinical-alerts/${alertId}/resolve`, { method: "POST" }),

  // Frontend longitudinal de Fase 1B (checkpoint posterior al backend,
  // 2026-08-23) — las 7 entidades de Migración 14.
  getClinicalProblems: (contactId: Id) => request<ClinicalProblem[]>(`/clinical/clinical-problems?contact_id=${contactId}`),
  createClinicalProblem: (payload: {
    contact_id: Id;
    problem_name: string;
    code?: string;
    severity?: string;
    onset_date?: string;
    notes?: string;
    created_by_professional_id?: Id;
  }) => request<ClinicalProblem>("/clinical/clinical-problems", { method: "POST", body: payload }),
  updateClinicalProblem: (id: Id, payload: { status?: ClinicalProblemStatus; resolved_date?: string; notes?: string }) =>
    request<ClinicalProblem>(`/clinical/clinical-problems/${id}`, { method: "PATCH", body: payload }),

  getAllergies: (contactId: Id) => request<Allergy[]>(`/clinical/allergies?contact_id=${contactId}`),
  createAllergy: (payload: {
    contact_id: Id;
    substance: string;
    reaction?: string;
    severity?: string;
    observed_at?: string;
    notes?: string;
    created_by_professional_id?: Id;
  }) => request<Allergy>("/clinical/allergies", { method: "POST", body: payload }),

  getActiveMedications: (contactId: Id) => request<ActiveMedication[]>(`/clinical/active-medications?contact_id=${contactId}`),
  createActiveMedication: (payload: {
    contact_id: Id;
    drug_name: string;
    dose?: string;
    frequency?: string;
    route?: string;
    indication?: string;
    started_at?: string;
    prescriber?: string;
    created_by_professional_id?: Id;
  }) => request<ActiveMedication>("/clinical/active-medications", { method: "POST", body: payload }),
  updateActiveMedication: (id: Id, payload: { status?: ActiveMedicationStatus; ended_at?: string }) =>
    request<ActiveMedication>(`/clinical/active-medications/${id}`, { method: "PATCH", body: payload }),

  getDiagnosticStudies: (contactId: Id) => request<DiagnosticStudy[]>(`/clinical/diagnostic-studies?contact_id=${contactId}`),
  createDiagnosticStudy: (payload: {
    contact_id: Id;
    study_type: string;
    title: string;
    encounter_id?: Id;
    medical_order_id?: Id;
    ordering_professional_id?: Id;
    ordered_at?: string;
    institution?: string;
  }) => request<DiagnosticStudy>("/clinical/diagnostic-studies", { method: "POST", body: payload }),
  updateDiagnosticStudy: (
    id: Id,
    payload: { status?: string; performed_at?: string; received_at?: string; textual_result?: string; file_id?: Id }
  ) => request<DiagnosticStudy>(`/clinical/diagnostic-studies/${id}`, { method: "PATCH", body: payload }),
  addStudyResults: (
    studyId: Id,
    values: { analyte_name: string; numeric_value?: number; text_value?: string; unit?: string; ref_low?: number; ref_high?: number; flag?: StudyResultFlag }[]
  ) => request<{ study: DiagnosticStudy; values: any[] }>(`/clinical/diagnostic-studies/${studyId}/results`, { method: "POST", body: { values } }),

  getAnthropometricMeasurements: (contactId: Id) =>
    request<AnthropometricMeasurement[]>(`/clinical/anthropometric-measurements?contact_id=${contactId}`),
  createAnthropometricMeasurement: (payload: {
    contact_id: Id;
    encounter_id?: Id;
    program_id?: Id;
    session_id?: Id;
    measured_at?: string;
    weight_kg?: number;
    height_cm?: number;
    waist_cm?: number;
    hip_cm?: number;
    body_fat_pct?: number;
    fat_mass_kg?: number;
    lean_mass_kg?: number;
    muscle_mass_kg?: number;
    water_pct?: number;
    visceral_fat?: number;
    measurement_context?:
      AnthropometricMeasurementContext;
    source_type?: AnthropometricSourceType;
    source_device?: string;
    metadata?: Record<string, unknown>;
    notes?: string;
    created_by_professional_id?: Id;
  }) => request<AnthropometricMeasurement>(
    "/clinical/anthropometric-measurements",
    { method: "POST", body: payload }
  ),

  getTreatmentProtocolTemplates: () => request<TreatmentProtocolTemplate[]>("/clinical/treatment-protocol-templates"),
  getTreatmentProtocolRecords: (contactId: Id) =>
    request<TreatmentProtocolRecord[]>(`/clinical/treatment-protocol-records?contact_id=${contactId}`),
  createTreatmentProtocolRecord: (payload: {
    contact_id: Id;
    template_id: Id;
    professional_id: Id;
    performed_at: string;
    data_json: Record<string, unknown>;
    encounter_id?: Id;
    service_id?: Id;
  }) => request<TreatmentProtocolRecord>("/clinical/treatment-protocol-records", { method: "POST", body: payload }),
  signTreatmentProtocolRecord: (id: Id) =>
    request<TreatmentProtocolRecord>(`/clinical/treatment-protocol-records/${id}/sign`, { method: "POST" }),

  getImplantDevices: (contactId: Id) => request<ImplantDeviceRecord[]>(`/clinical/implant-device-registry?contact_id=${contactId}`),
  createImplantDevice: (payload: {
    contact_id: Id;
    item_type: string;
    brand?: string;
    model?: string;
    lot_number?: string;
    serial_number?: string;
    expiry_date?: string;
    implanted_at?: string;
    notes?: string;
  }) => request<ImplantDeviceRecord>("/clinical/implant-device-registry", { method: "POST", body: payload }),

  updatePatientPregnancyStatus: (contactId: Id, pregnancy_status: string) =>
    request<PatientMedicalProfile>(`/clinical/patients/${contactId}/medical-profile`, { method: "PATCH", body: { pregnancy_status } }),

  createPrescription: (payload: {
    contact_id: Id;
    encounter_id?: Id;
    professional_id: Id;
    instructions?: string;
    items: { medication_name: string; presentation?: string; dose?: string; frequency?: string; duration?: string; item_instructions?: string }[];
  }) => request<Prescription>("/clinical/prescriptions", { method: "POST", body: payload }),
  signPrescription: (id: Id) => request<Prescription>(`/clinical/prescriptions/${id}/sign`, { method: "POST" }),

  createMedicalOrder: (payload: {
    contact_id: Id;
    encounter_id?: Id;
    professional_id: Id;
    order_type: string;
    indication: string;
    priority?: string;
    notes?: string;
  }) => request<MedicalOrder>("/clinical/medical-orders", { method: "POST", body: payload }),
  signMedicalOrder: (id: Id) => request<MedicalOrder>(`/clinical/medical-orders/${id}/sign`, { method: "POST" }),

  createConsent: (payload: {
    contact_id: Id;
    template_code: string;
    encounter_id?: Id;
    appointment_id?: Id;
    professional_id?: Id;
  }) => request<PatientConsent>("/clinical/consents", { method: "POST", body: payload }),
  // El paciente es el firmante del consentimiento, no el usuario interno
  // logueado — este endpoint REGISTRA que se recibió una firma (papel,
  // verbal presenciada, documento firmado), no "firma en nombre de".
  // signature_method y evidence_note son obligatorios (ver clinical.js).
  recordConsentSignature: (
    id: Id,
    payload: { signature_method: "IN_PERSON_PAPER" | "IN_PERSON_VERBAL_WITNESSED" | "RECEIVED_SIGNED_DOCUMENT" | "PATIENT_PORTAL_ESIGN"; evidence_note: string; signed_document_storage_key?: string }
  ) => request<PatientConsent>(`/clinical/consents/${id}/record-signature`, { method: "POST", body: payload }),

  attachClinicalFile: (
    contactId: Id,
    payload: {
      encounter_id?: Id;
      service_id?: Id;
      professional_id?: Id;
      file_type: string;
      storage_key: string;
      mime_type?: string;
      zone?: string;
      label?: string;
      marketing_use_consent?: boolean;
      clinical_visibility?: string;
    }
  ) => request<PatientFile>(`/clinical/patients/${contactId}/files`, { method: "POST", body: payload }),

  // ---- Archivos clínicos privados administrados ----
  initializePrivateClinicalFile: (
    contactId: Id,
    payload: {
      file_type: string;
      original_filename: string;
      mime_type: string;
      size_bytes: number;
      encounter_id?: Id;
      service_id?: Id;
      professional_id?: Id;
      program_id?: Id;
      session_id?: Id;
      diagnostic_study_id?: Id;
      outcome_assessment_id?: Id;
      zone?: string;
      captured_at?: string;
      label?: string;
      marketing_use_consent?: boolean;
      clinical_visibility?:
        "CLINICAL" | "MARKETING" | "BOTH";
      capture_phase?:
        "BEFORE" | "DURING" | "AFTER" | "FOLLOW_UP";
      view_code?: string;
      pose_code?: string;
      protocol_code?: string;
      comparison_ready?: boolean;
      patient_visibility?:
        ClinicalPatientVisibility;
      metadata?: Record<string, unknown>;
    }
  ) =>
    request<ClinicalPrivateFileInitialization>(
      `/clinical/patients/${contactId}/files/private`,
      {
        method: "POST",
        body: payload,
      }
    ),

  uploadPrivateClinicalFileContent: async (
    contactId: Id,
    fileId: Id,
    file: Blob
  ): Promise<PatientFile> => {
    const response = await binaryRequest(
      `/clinical/patients/${contactId}/files/${fileId}/content`,
      {
        method: "PUT",
        body: file,
        mimeType:
          file.type ||
          "application/octet-stream",
      }
    );

    return response.json() as
      Promise<PatientFile>;
  },

  downloadPrivateClinicalFile: async (
    contactId: Id,
    fileId: Id
  ): Promise<{
    blob: Blob;
    filename: string;
    mimeType: string;
  }> => {
    const response = await binaryRequest(
      `/clinical/patients/${contactId}/files/${fileId}/content`
    );

    const disposition =
      response.headers.get(
        "content-disposition"
      ) || "";

    const utf8Match = disposition.match(
      /filename\*=UTF-8''([^;]+)/
    );

    const simpleMatch = disposition.match(
      /filename="([^"]+)"/
    );

    let filename =
      `archivo-clinico-${fileId}`;

    try {
      filename = decodeURIComponent(
        utf8Match?.[1] ||
        simpleMatch?.[1] ||
        filename
      );
    } catch {
      // Conserva el nombre seguro de respaldo.
    }

    const blob = await response.blob();

    return {
      blob,
      filename,
      mimeType:
        response.headers.get(
          "content-type"
        ) ||
        blob.type ||
        "application/octet-stream",
    };
  },

  // ---- Historia Clínica 360: programas y comparaciones ----
  getClinicalCarePrograms: (contactId: Id) =>
    request<ClinicalCareProgramListResponse>(
      "/clinical/care-programs",
      {
        query: {
          contact_id: String(contactId),
        },
      }
    ),

  getClinicalCareProgram: (programId: Id) =>
    request<ClinicalCareProgramDetailResponse>(
      `/clinical/care-programs/${programId}`
    ),

  getClinical360Report: (
    programId: Id,
    reportId: Id
  ) =>
    request<{
      report: Clinical360Report;
      integrity_valid: boolean;
    }>(
      `/clinical/care-programs/${programId}/reports/${reportId}`
    ),

  createClinical360Report: (
    programId: Id,
    payload: {
      report_type?: Clinical360ReportType;
      title: string;
      purpose?: string;
      clinical_summary?: string;
      professional_conclusion?: string;
      included_sections:
        Clinical360ReportSection[];
      professional_id: Id;
      patient_visibility?:
        ClinicalPatientVisibility;
      supersedes_report_id?: Id;
    }
  ) =>
    request<Clinical360Report>(
      `/clinical/care-programs/${programId}/reports`,
      {
        method: "POST",
        body: payload,
      }
    ),

  updateClinical360Report: (
    programId: Id,
    reportId: Id,
    payload: {
      title?: string;
      purpose?: string;
      clinical_summary?: string;
      professional_conclusion?: string;
      included_sections?:
        Clinical360ReportSection[];
      patient_visibility?:
        ClinicalPatientVisibility;
    }
  ) =>
    request<Clinical360Report>(
      `/clinical/care-programs/${programId}/reports/${reportId}`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  signClinical360Report: (
    programId: Id,
    reportId: Id
  ) =>
    request<Clinical360Report>(
      `/clinical/care-programs/${programId}/reports/${reportId}/sign`,
      {
        method: "POST",
        body: {
          confirm: true,
        },
      }
    ),

  downloadClinical360Report: async (
    programId: Id,
    reportId: Id,
    format: "html" | "json"
  ): Promise<{
    blob: Blob;
    filename: string;
    mimeType: string;
  }> => {
    const response =
      await binaryRequest(
        `/clinical/care-programs/${programId}/reports/${reportId}/export?format=${format}`
      );

    const disposition =
      response.headers.get(
        "content-disposition"
      ) || "";

    const utf8Match =
      disposition.match(
        /filename\*=UTF-8''([^;]+)/
      );

    const simpleMatch =
      disposition.match(
        /filename="([^"]+)"/
      );

    let filename =
      `informe-clinico-${reportId}.${format}`;

    try {
      filename =
        decodeURIComponent(
          utf8Match?.[1] ||
          simpleMatch?.[1] ||
          filename
        );
    } catch {
      // Conserva el nombre seguro de respaldo.
    }

    const blob =
      await response.blob();

    return {
      blob,
      filename,
      mimeType:
        response.headers.get(
          "content-type"
        ) ||
        blob.type ||
        "application/octet-stream",
    };
  },

  createClinicalComparison: (
    programId: Id,
    payload: {
      comparison_type: ClinicalComparisonType;
      title: string;
      clinical_question?: string;
      baseline_at?: string;
      comparison_at?: string;
      conclusion?: string;
      observed_changes?:
        Record<string, unknown>;
      possible_contributors?:
        Record<string, unknown>;
      patient_visibility?:
        ClinicalPatientVisibility;
      items: Array<{
        item_role:
          ClinicalComparisonItemRole;
        patient_file_id?: Id;
        diagnostic_study_id?: Id;
        anthropometric_measurement_id?: Id;
        outcome_assessment_id?: Id;
        session_id?: Id;
        position?: number;
        notes?: string;
      }>;
    }
  ) =>
    request<ClinicalComparisonSet>(
      `/clinical/care-programs/${programId}/comparisons`,
      {
        method: "POST",
        body: payload,
      }
    ),

  signClinicalComparison: (
    programId: Id,
    comparisonId: Id
  ) =>
    request<ClinicalComparisonSet>(
      `/clinical/care-programs/${programId}/comparisons/${comparisonId}/sign`,
      {
        method: "POST",
        body: {
          confirm: true,
        },
      }
    ),

  createClinicalAnatomicalMap: (
    programId: Id,
    payload: {
      session_id?: Id;
      encounter_id?: Id;
      map_type: ClinicalAnatomicalMapType;
      view_code: string;
      purpose: ClinicalAnatomicalMapPurpose;
      template_code: string;
      template_version?: number;
      notes?: string;
      patient_visibility?:
        ClinicalPatientVisibility;
      markers: Array<{
        marker_key: string;
        marker_type:
          ClinicalAnatomicalMarkerType;
        geometry_type:
          ClinicalGeometryType;
        normalized_geometry:
          Record<string, unknown>;
        zone_code?: string;
        laterality?: ClinicalLaterality;
        label?: string;
        diagnosis_text?: string;
        planned_treatment?:
          Record<string, unknown>;
        administration_id?: Id;
        clinical_details?:
          Record<string, unknown>;
      }>;
    }
  ) =>
    request<ClinicalAnatomicalMap>(
      `/clinical/care-programs/${programId}/anatomical-maps`,
      {
        method: "POST",
        body: payload,
      }
    ),

  signClinicalAnatomicalMap: (
    programId: Id,
    mapId: Id
  ) =>
    request<ClinicalAnatomicalMap>(
      `/clinical/care-programs/${programId}/anatomical-maps/${mapId}/sign`,
      {
        method: "POST",
        body: {
          confirm: true,
        },
      }
    ),

  // Inventario operativo.
  getInventoryBootstrap: () => request<any>("/inventory/bootstrap"),
  getInventoryStock: (warehouseId?: Id) =>
    request<any[]>("/inventory/stock", {
      query: warehouseId ? { warehouse_id: String(warehouseId) } : undefined,
    }),
  getInventoryMovements: () => request<any[]>("/inventory/movements"),
  createInventorySupplier: (payload: Record<string, unknown>) =>
    request<any>("/inventory/suppliers", { method: "POST", body: payload }),
  createInventoryWarehouse: (payload: Record<string, unknown>) =>
    request<any>("/inventory/warehouses", { method: "POST", body: payload }),
  createInventoryProduct: (payload: Record<string, unknown>) =>
    request<any>("/inventory/products", { method: "POST", body: payload }),
  createInventoryReceipt: (payload: Record<string, unknown>) =>
    request<any>("/inventory/receipts", { method: "POST", body: payload }),

  // ---- Frontend Operativo de Fase 2 + Fase 2B (EN DESARROLLO) ----
  // Ejecución del tratamiento: registro real de insumos + equipamiento, y
  // lectura combinada de lo registrado + lo esperado (plantilla).
  getTreatmentRecord: (encounterId: Id, serviceIdOverride?: Id) =>
    request<TreatmentRecordView>(`/clinical/encounters/${encounterId}/treatment-record`, {
      query: serviceIdOverride ? { service_id: String(serviceIdOverride) } : undefined,
    }),
  registerTreatmentRecord: (
    encounterId: Id,
    payload: {
      idempotency_key: string;
      insumos?: TreatmentRecordInsumoInput[];
      equipment_usage?: TreatmentRecordEquipmentInput[];
    }
  ) => request<TreatmentRecordView & { replayed: boolean }>(`/clinical/encounters/${encounterId}/treatment-record`, {
    method: "POST",
    body: payload,
  }),

  // Configuración del tratamiento: CRUD acotado de plantillas por
  // servicio (admin/director) + lookups de sólo lectura para poblar los
  // selectores (mismos permisos — no es catálogo abierto).
  getServiceTemplates: (serviceId: Id) => request<ServiceTemplatesResponse>(`/clinical/service-templates/${serviceId}`),
  saveConsumptionTemplate: (serviceId: Id, items: { product_id: Id; quantity: number; required?: boolean }[]) =>
    request<ConsumptionTemplate>(`/clinical/service-templates/${serviceId}/consumption`, {
      method: "POST",
      body: { items },
    }),
  saveEquipmentTemplate: (
    serviceId: Id,
    items: { asset_id: Id; capacity_unit: string; expected_quantity: number; required?: boolean }[]
  ) =>
    request<EquipmentTemplate>(`/clinical/service-templates/${serviceId}/equipment`, {
      method: "POST",
      body: { items },
    }),
  // Catálogo administrativo de servicios/tratamientos.
  getServices: () =>
    request<{ id: Id; code: string; name: string; family: string | null; active: boolean }[]>("/services"),

  createService: (payload: { code: string; name: string; family?: string | null }) =>
    request<{ id: Id; code: string; name: string; family: string | null; active: boolean }>("/services", {
      method: "POST",
      body: payload,
    }),

  updateService: (
    serviceId: Id,
    payload: { name?: string; family?: string | null; active?: boolean }
  ) =>
    request<{ id: Id; code: string; name: string; family: string | null; active: boolean }>(
      `/services/${serviceId}`,
      {
        method: "PATCH",
        body: payload,
      }
    ),

  getServicesLookup: () => request<{ id: Id; code: string; name: string }[]>("/clinical/lookups/services"),
  getInventoryProductsLookup: () =>
    request<{ id: Id; sku: string; name: string; product_type: string; lot_control: boolean; serial_control: boolean }[]>(
      "/clinical/lookups/inventory-products"
    ),
  getEquipmentAssetsLookup: () =>
    request<{ id: Id; asset_tag: string; name: string }[]>("/clinical/lookups/equipment-assets"),

  // Lookups de sólo lectura para la pantalla de EJECUCIÓN.
  getWarehousesLookup: () => request<{ id: Id; code: string; name: string }[]>("/clinical/lookups/warehouses"),
  getInventoryLotsLookup: (productId: Id) =>
    request<
      { lot_id: Id; lot_number: string | null; serial_number: string | null; unit_cost: string | number; warehouse_id: Id; available_quantity: string | number }[]
    >("/clinical/lookups/inventory-lots", { query: { product_id: String(productId) } }),
  getEquipmentComponentsLookup: (assetId: Id) =>
    request<
      { id: Id; asset_id: Id; capacity_unit: string; capacity_total: string | number; unit_cost: string | number; installed_at: string }[]
    >("/clinical/lookups/equipment-components", { query: { asset_id: String(assetId) } }),
};
